import React, { useState, useEffect } from 'react';
import { Table, Spinner, Alert, Badge, Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input } from '../../shim/reactstrap';
import moment from 'moment';
import apiClient from '../../api/ApiClient';
import { readPowerUserMode } from '../Profile';

// Format EmailVerifiedAt into a human-friendly tooltip in the
// browser's local timezone. The server sends UTC (DateTimeKind.Utc),
// so moment without an explicit zone would render the UTC clock time
// — which is misleading for an admin who just wants to know when it
// was verified relative to "now".
const formatEmailVerifiedTooltip = (value) => {
  if (!value) return null;
  const m = moment.utc(value);
  if (!m.isValid()) return null;
  return `Verified at ${m.local().format('YYYY-MM-DD HH:mm:ss z')}`;
};

export const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [powerUserMode, setPowerUserMode] = useState(() => readPowerUserMode());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    Email: '',
    Name: '',
    Enabled: true,
    Roles: []
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [generatedPassword, setGeneratedPassword] = useState(null);

  // Edit user state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    Name: '',
    Enabled: true,
    Roles: []
  });
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  // Generate password state
  const [generatingPasswordFor, setGeneratingPasswordFor] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState(null);

  // Delete state
  const [deletingUserId, setDeletingUserId] = useState(null);

  // Email-verified toggle state
  const [updatingEmailVerifiedFor, setUpdatingEmailVerifiedFor] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  // Re-read the Power User Mode flag whenever the page regains focus,
  // so an admin who toggled it from the Profile page sees the
  // privileged columns light up here without a manual reload.
  useEffect(() => {
    const refreshPowerMode = () => setPowerUserMode(readPowerUserMode());
    window.addEventListener('focus', refreshPowerMode);
    return () => window.removeEventListener('focus', refreshPowerMode);
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.searchUsers({ Limit: 10000, Offset: 0 });
      setUsers(data.Items || []);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    if (!createForm.Email || !createForm.Name) {
      setCreateError('Email and Name are required');
      return;
    }

    try {
      setCreating(true);
      setCreateError(null);
      const result = await apiClient.createUser(createForm);
      setGeneratedPassword(result.Result);

      // Reload users list
      await loadUsers();

      // Reset form
      setCreateForm({
        Email: '',
        Name: '',
        Enabled: true,
        Roles: []
      });
    } catch (err) {
      console.error('Failed to create user:', err);
      setCreateError(err.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleCloseModal = () => {
    // Close modal first
    setShowCreateModal(false);

    // Reset states after a short delay to prevent flickering
    setTimeout(() => {
      setCreateError(null);
      setGeneratedPassword(null);
      setCreateForm({
        Email: '',
        Name: '',
        Enabled: true,
        Roles: []
      });
    }, 300);
  };

  const handleRoleToggle = (role) => {
    setCreateForm(prev => ({
      ...prev,
      Roles: prev.Roles.includes(role)
        ? prev.Roles.filter(r => r !== role)
        : [...prev.Roles, role]
    }));
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      Name: user.Name,
      Enabled: user.Enabled,
      Roles: user.Roles || []
    });
    setUpdateError(null);
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();

    if (!editForm.Name) {
      setUpdateError('Name is required');
      return;
    }

    try {
      setUpdating(true);
      setUpdateError(null);
      await apiClient.updateUser(editingUser.Id, editForm);

      // Reload users list
      await loadUsers();

      // Close modal
      setShowEditModal(false);
      setEditingUser(null);
    } catch (err) {
      console.error('Failed to update user:', err);
      setUpdateError(err.message || 'Failed to update user');
    } finally {
      setUpdating(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setTimeout(() => {
      setEditingUser(null);
      setUpdateError(null);
      setEditForm({
        Name: '',
        Enabled: true,
        Roles: []
      });
    }, 300);
  };

  const handleEditRoleToggle = (role) => {
    setEditForm(prev => ({
      ...prev,
      Roles: prev.Roles.includes(role)
        ? prev.Roles.filter(r => r !== role)
        : [...prev.Roles, role]
    }));
  };

  const handleGeneratePassword = async (user) => {
    if (!window.confirm(`Generate a new password for ${user.Name}?`)) {
      return;
    }

    try {
      setGeneratingPasswordFor(user.Id);
      const result = await apiClient.generateUserPassword(user.Id);
      setNewPassword(result.Result);
      setShowPasswordModal(true);
    } catch (err) {
      console.error('Failed to generate password:', err);
      alert(err.message || 'Failed to generate password');
    } finally {
      setGeneratingPasswordFor(null);
    }
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setTimeout(() => {
      setNewPassword(null);
    }, 300);
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete user "${user.Name}" (${user.Email})? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingUserId(user.Id);
      await apiClient.deleteUser(user.Id);
      await loadUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert(err.message || 'Failed to delete user');
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleToggleEmailVerified = async (user) => {
    const nextValue = !user.EmailVerified;
    const confirmMessage = nextValue
      ? `Mark email as verified for "${user.Name}" (${user.Email})?`
      : `Mark email as UNVERIFIED for "${user.Name}" (${user.Email})?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setUpdatingEmailVerifiedFor(user.Id);
      await apiClient.setUserEmailVerified(user.Id, nextValue);
      // Reflect the change locally so the UI is responsive; reload to
      // pick up server-side EmailVerifiedAt as well.
      setUsers(prev => prev.map(u => u.Id === user.Id ? { ...u, EmailVerified: nextValue } : u));
      await loadUsers();
    } catch (err) {
      console.error('Failed to update email-verified flag:', err);
      alert(err.message || 'Failed to update email-verified flag');
    } finally {
      setUpdatingEmailVerifiedFor(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner color="primary" />
        <div className="mt-2">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert color="danger" fade={false}>
        <h5>Error</h5>
        <p>{error}</p>
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Users</h2>
        <div className="d-flex align-items-center gap-3">
          <div className="text-muted">Total: {users.length}</div>
          <Button color="primary" onClick={() => setShowCreateModal(true)}>
            Create New User
          </Button>
        </div>
      </div>

      <div className="card">
        <div className="card-body p-0">
          <Table striped hover responsive className="mb-0">
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>Name</th>
                <th>Google ID</th>
                <th>Enabled</th>
                <th>Email Verified</th>
                <th>Roles</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center text-muted">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.Id}>
                    <td>{user.Id}</td>
                    <td className="text-truncate" style={{ maxWidth: '220px' }} title={user.Email}>
                      {user.Email}
                    </td>
                    <td className="text-truncate" style={{ maxWidth: '280px' }} title={user.Name}>
                      {user.Name}
                    </td>
                    <td>
                      {user.GoogleId ? (
                        <Badge color="info" title={user.GoogleId}>
                          Linked
                        </Badge>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {user.Enabled ? (
                        <Badge color="success">Enabled</Badge>
                      ) : (
                        <Badge color="secondary">Disabled</Badge>
                      )}
                    </td>
                    <td>
                      {user.EmailVerified ? (
                        powerUserMode ? (
                          <Button
                            size="sm"
                            color="success"
                            outline
                            onClick={() => handleToggleEmailVerified(user)}
                            disabled={updatingEmailVerifiedFor === user.Id}
                            title={(() => {
                              const ts = formatEmailVerifiedTooltip(user.EmailVerifiedAt);
                              return ts
                                ? `${ts} — Power User Mode: click to mark as unverified`
                                : 'Power User Mode: click to mark as unverified';
                            })()}
                          >
                            {updatingEmailVerifiedFor === user.Id ? '...' : 'Verified'}
                          </Button>
                        ) : (
                          <Badge
                            color="success"
                            title={formatEmailVerifiedTooltip(user.EmailVerifiedAt) ?? 'Email is verified'}
                          >
                            Verified
                          </Badge>
                        )
                      ) : (
                        powerUserMode ? (
                          <Button
                            size="sm"
                            color="warning"
                            outline
                            onClick={() => handleToggleEmailVerified(user)}
                            disabled={updatingEmailVerifiedFor === user.Id}
                            title="Power User Mode: click to mark as verified"
                          >
                            {updatingEmailVerifiedFor === user.Id ? '...' : 'Unverified'}
                          </Button>
                        ) : (
                          <Badge color="warning" title="Email is not verified">Unverified</Badge>
                        )
                      )}
                    </td>
                    <td>
                      {user.Roles && user.Roles.length > 0 ? (
                        user.Roles.map((role, idx) => (
                          <Badge key={idx} color="primary" className="me-1">
                            {role}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted">No roles</span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          color="primary"
                          onClick={() => handleEditUser(user)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          color="warning"
                          onClick={() => handleGeneratePassword(user)}
                          disabled={generatingPasswordFor === user.Id}
                        >
                          {generatingPasswordFor === user.Id ? 'Generating...' : 'New Password'}
                        </Button>
                        <Button
                          size="sm"
                          color="danger"
                          onClick={() => handleDeleteUser(user)}
                          disabled={deletingUserId === user.Id}
                        >
                          {deletingUserId === user.Id ? 'Deleting...' : 'Delete'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>

      <Modal isOpen={showCreateModal} toggle={handleCloseModal}>
        <ModalHeader toggle={handleCloseModal}>Create New User</ModalHeader>
        <ModalBody>
          {createError && (
            <Alert color="danger" className="mb-3" fade={false}>
              {createError}
            </Alert>
          )}

          {generatedPassword ? (
            <Alert color="success" fade={false}>
              <h5>User Created Successfully!</h5>
              <p className="mb-2">The temporary password for this user is:</p>
              <div className="p-3 bg-light rounded">
                <code className="fs-5">{generatedPassword}</code>
              </div>
              <p className="mt-2 mb-0 small">
                Please save this password and share it securely with the user. It will not be shown again.
              </p>
            </Alert>
          ) : (
            <Form onSubmit={handleCreateUser}>
              <FormGroup>
                <Label for="email">Email *</Label>
                <Input
                  type="email"
                  id="email"
                  value={createForm.Email}
                  onChange={(e) => setCreateForm({ ...createForm, Email: e.target.value })}
                  required
                  disabled={creating}
                />
              </FormGroup>

              <FormGroup>
                <Label for="name">Name *</Label>
                <Input
                  type="text"
                  id="name"
                  value={createForm.Name}
                  onChange={(e) => setCreateForm({ ...createForm, Name: e.target.value })}
                  required
                  disabled={creating}
                />
              </FormGroup>

              <FormGroup check className="mb-3">
                <Input
                  type="checkbox"
                  id="enabled"
                  checked={createForm.Enabled}
                  onChange={(e) => setCreateForm({ ...createForm, Enabled: e.target.checked })}
                  disabled={creating}
                />
                <Label check for="enabled">
                  Enabled
                </Label>
              </FormGroup>

              <FormGroup>
                <Label>Roles</Label>
                <div>
                  <FormGroup check>
                    <Input
                      type="checkbox"
                      id="role-admin"
                      checked={createForm.Roles.includes('Admin')}
                      onChange={() => handleRoleToggle('Admin')}
                      disabled={creating}
                    />
                    <Label check for="role-admin">
                      Admin
                    </Label>
                  </FormGroup>
                  <FormGroup check>
                    <Input
                      type="checkbox"
                      id="role-package-data-consumer"
                      checked={createForm.Roles.includes('PackageDataConsumer')}
                      onChange={() => handleRoleToggle('PackageDataConsumer')}
                      disabled={creating}
                    />
                    <Label check for="role-package-data-consumer">
                      Package Data Consumer
                    </Label>
                  </FormGroup>
                  <FormGroup check>
                    <Input
                      type="checkbox"
                      id="role-spreadsheet-sharing"
                      checked={createForm.Roles.includes('SpreadsheetSharing')}
                      onChange={() => handleRoleToggle('SpreadsheetSharing')}
                      disabled={creating}
                    />
                    <Label check for="role-spreadsheet-sharing">
                      Spreadsheet Sharing
                    </Label>
                  </FormGroup>
                </div>
              </FormGroup>
            </Form>
          )}
        </ModalBody>
        <ModalFooter>
          {generatedPassword ? (
            <Button color="primary" onClick={handleCloseModal}>
              Close
            </Button>
          ) : (
            <>
              <Button color="secondary" onClick={handleCloseModal} disabled={creating}>
                Cancel
              </Button>
              <Button color="primary" onClick={handleCreateUser} disabled={creating}>
                {creating ? 'Creating...' : 'Create User'}
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>

      {/* Edit User Modal */}
      <Modal isOpen={showEditModal} toggle={handleCloseEditModal}>
        <ModalHeader toggle={handleCloseEditModal}>Edit User</ModalHeader>
        <ModalBody>
          {updateError && (
            <Alert color="danger" className="mb-3" fade={false}>
              {updateError}
            </Alert>
          )}

          {editingUser && (
            <Form onSubmit={handleUpdateUser}>
              <FormGroup>
                <Label>Email</Label>
                <Input
                  type="text"
                  value={editingUser.Email}
                  disabled
                  readOnly
                />
                <small className="text-muted">Email cannot be changed</small>
              </FormGroup>

              <FormGroup>
                <Label for="edit-name">Name *</Label>
                <Input
                  type="text"
                  id="edit-name"
                  value={editForm.Name}
                  onChange={(e) => setEditForm({ ...editForm, Name: e.target.value })}
                  required
                  disabled={updating}
                />
              </FormGroup>

              <FormGroup check className="mb-3">
                <Input
                  type="checkbox"
                  id="edit-enabled"
                  checked={editForm.Enabled}
                  onChange={(e) => setEditForm({ ...editForm, Enabled: e.target.checked })}
                  disabled={updating}
                />
                <Label check for="edit-enabled">
                  Enabled
                </Label>
              </FormGroup>

              <FormGroup>
                <Label>Roles</Label>
                <div>
                  <FormGroup check>
                    <Input
                      type="checkbox"
                      id="edit-role-admin"
                      checked={editForm.Roles.includes('Admin')}
                      onChange={() => handleEditRoleToggle('Admin')}
                      disabled={updating}
                    />
                    <Label check for="edit-role-admin">
                      Admin
                    </Label>
                  </FormGroup>
                  <FormGroup check>
                    <Input
                      type="checkbox"
                      id="edit-role-package-data-consumer"
                      checked={editForm.Roles.includes('PackageDataConsumer')}
                      onChange={() => handleEditRoleToggle('PackageDataConsumer')}
                      disabled={updating}
                    />
                    <Label check for="edit-role-package-data-consumer">
                      Package Data Consumer
                    </Label>
                  </FormGroup>
                  <FormGroup check>
                    <Input
                      type="checkbox"
                      id="edit-role-spreadsheet-sharing"
                      checked={editForm.Roles.includes('SpreadsheetSharing')}
                      onChange={() => handleEditRoleToggle('SpreadsheetSharing')}
                      disabled={updating}
                    />
                    <Label check for="edit-role-spreadsheet-sharing">
                      Spreadsheet Sharing
                    </Label>
                  </FormGroup>
                </div>
              </FormGroup>
            </Form>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCloseEditModal} disabled={updating}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleUpdateUser} disabled={updating}>
            {updating ? 'Updating...' : 'Update User'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Generated Password Modal */}
      <Modal isOpen={showPasswordModal} toggle={handleClosePasswordModal}>
        <ModalHeader toggle={handleClosePasswordModal}>New Password Generated</ModalHeader>
        <ModalBody>
          <Alert color="success" fade={false}>
            <h5>Password Generated Successfully!</h5>
            <p className="mb-2">The new password is:</p>
            <div className="p-3 bg-light rounded">
              <code className="fs-5">{newPassword}</code>
            </div>
            <p className="mt-2 mb-0 small">
              Please save this password and share it securely with the user. It will not be shown again.
            </p>
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleClosePasswordModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

